import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Send, Eye, Heart, Calendar, DollarSign, Users, RefreshCw } from "lucide-react";

export default function AdminDigest() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const generatePreview = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/digest/preview`, { withCredentials: true });
      setDigest(res.data);
    } catch { toast.error("Failed to generate digest preview"); }
    finally { setLoading(false); }
  };

  const sendDigest = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/digest/send`, {}, { withCredentials: true });
      if (res.data.success) {
        toast.success(`Digest sent to ${res.data.sent}/${res.data.total_subscribers} subscribers!`);
      } else {
        toast.error(res.data.message || "Failed to send digest");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  return (
    <AdminLayout currentPage="Digest">
      <div className="space-y-6" data-testid="admin-digest-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-deep-blue">Weekly Community Digest</h1>
            <p className="text-slate-500 mt-1">Preview and send the weekly email digest to all subscribers</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={generatePreview} disabled={loading} variant="outline" data-testid="preview-digest-btn">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              {loading ? "Generating..." : "Generate Preview"}
            </Button>
            {digest && (
              <Button onClick={sendDigest} disabled={sending} className="bg-deep-blue text-white" data-testid="send-digest-btn">
                <Send className="w-4 h-4 mr-2" /> {sending ? "Sending..." : "Send to Subscribers"}
              </Button>
            )}
          </div>
        </div>

        {!digest ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
            <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600">No digest generated yet</h3>
            <p className="text-slate-400 mt-1 mb-4">Click "Generate Preview" to see what will be sent</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Stats */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                  <Users className="w-8 h-8 text-awareness-blue mx-auto mb-2" />
                  <div className="text-2xl font-bold text-deep-blue">{digest.subscriber_count}</div>
                  <div className="text-xs text-slate-500">Subscribers</div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                  <Users className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-deep-blue">{digest.new_members}</div>
                  <div className="text-xs text-slate-500">New Members (7d)</div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-bold text-deep-blue mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" /> Top Community Posts
                </h3>
                {digest.top_posts.length === 0 ? (
                  <p className="text-slate-400 text-sm">No posts this week</p>
                ) : (
                  <div className="space-y-3">
                    {digest.top_posts.map((p, i) => (
                      <div key={i} className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm text-deep-blue">{p.user_name}</span>
                          <span className="text-xs text-slate-400">{p.likes_count || 0} likes</span>
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-2">{p.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-bold text-deep-blue mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-awareness-blue" /> Upcoming Events
                </h3>
                {digest.upcoming_events.length === 0 ? (
                  <p className="text-slate-400 text-sm">No upcoming events</p>
                ) : (
                  <div className="space-y-3">
                    {digest.upcoming_events.map((e, i) => (
                      <div key={i} className="bg-blue-50 rounded-lg p-3">
                        <span className="font-semibold text-sm text-deep-blue">{e.title}</span>
                        <p className="text-xs text-slate-500 mt-0.5">{e.location}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-bold text-deep-blue mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-500" /> Donation Summary
                </h3>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="text-xl font-bold text-green-700">${digest.donation_summary.total.toFixed(0)}</div>
                    <div className="text-xs text-green-600">Total Raised (7d)</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="text-xl font-bold text-green-700">{digest.donation_summary.count}</div>
                    <div className="text-xs text-green-600">Donations</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Email Preview */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="bg-slate-100 px-4 py-3 border-b flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-600">Email Preview</span>
              </div>
              <div className="p-6" data-testid="digest-email-preview">
                <div className="bg-[#0a1e3d] text-white rounded-t-xl p-6 text-center">
                  <h2 className="text-xl font-bold">Blue vs Blue Weekly Digest</h2>
                  <p className="text-[#7eb8da] text-sm mt-1">Your weekly update from the community</p>
                </div>
                <div className="border border-slate-200 border-t-0 p-6 space-y-6">
                  <div>
                    <h3 className="font-bold text-deep-blue mb-3">Top Community Posts</h3>
                    {digest.top_posts.slice(0, 3).map((p, i) => (
                      <div key={i} className="bg-slate-50 rounded-lg p-3 mb-2">
                        <strong className="text-sm">{p.user_name}</strong>
                        <p className="text-sm text-slate-600 mt-0.5">{p.content?.substring(0, 200)}</p>
                        <small className="text-slate-400">{p.likes_count || 0} likes</small>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h3 className="font-bold text-deep-blue mb-3">Upcoming Events</h3>
                    {digest.upcoming_events.slice(0, 3).map((e, i) => (
                      <div key={i} className="bg-blue-50 rounded-lg p-3 mb-2">
                        <strong className="text-sm">{e.title}</strong>
                        <p className="text-xs text-slate-500">{e.location}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h3 className="font-bold text-deep-blue mb-3">This Week's Impact</h3>
                    <p className="text-sm"><strong>${digest.donation_summary.total.toFixed(0)}</strong> raised from <strong>{digest.donation_summary.count}</strong> donations</p>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-b-xl p-4 text-center border border-t-0 border-slate-200">
                  <p className="text-xs text-slate-400">Blue vs Blue Prostate Cancer Awareness Organization</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
